import { t } from '@lingui/macro';
import { useState, useEffect } from "react";
import { Message } from "../../../models/Message";
import { Room } from "../../../models/Room";
import { ReactionType } from "../../../models/local/Types";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { UIMessage } from "../UIMessage";
import { useToast } from "../../../components/ui/use-toast";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

interface IMsgInputFooterReactions { room: Room, parentMessage: UIMessage, onReactionUpdate?: () => void };

const MsgInputFooterReactions: React.FC<IMsgInputFooterReactions> = ({ room, parentMessage, onReactionUpdate }) => {
  const { toast } = useToast();

  // State 대신 렌더링 시마다 직접 계산하여 항상 최신 데이터 반영 (Sync 문제 해결)
  const myReactionMsg = parentMessage.reactions().find((m) => m.msg.user.id == My.user.id);
  const myReaction = myReactionMsg?.msg.emoticon;

  return (
    <div className="flex items-center gap-1 px-1">
      {getIcon('smile')}
      {getIcon('thumbs-up')}
      {getIcon('heart')}
      {getIcon('check')}
      {getIcon('shake-hand')}
    </div>
  )

  function getIcon(icon: ReactionType) {
    const isSaved = myReaction === icon;
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          sendReaction(icon);
        }}
        className={cn(
          "h-8 w-8 p-1 rounded-[10px] hover:bg-gray-100 transition-colors",
          isSaved && "border-2 border-[#FF8709] bg-[rgba(94,195,179,0.15)] hover:bg-[rgba(94,195,179,0.25)]"
        )}
      >
        <img
          src={`/assets/icon/ic-${icon}-24.svg`}
          alt={icon}
          className="w-full h-full"
        />
      </Button>
    );
  }

  async function sendReaction(reaction: ReactionType) {
    // 1. 현재 내 아이디로 된 모든 리액션 메시지 객체 찾기
    const myReactions = parentMessage.children.filter(
      m => m.msg.user.id === My.user.id && !!m.msg.emoticon
    );

    // 2. UI에서 즉시 제거 (Optimistic Update)
    if (myReactions.length > 0) {
      parentMessage.children = parentMessage.children.filter(
        m => !(m.msg.user.id === My.user.id && !!m.msg.emoticon)
      );
    }

    // 3. 서버에서 삭제 (비동기, 병렬, 에러 무시)
    const deletePromises = myReactions.map(async (m) => {
      try {
        await Fire.msg.deleteMessage(m.msg.id);
      } catch (e) {
        // 이미 삭제되었거나 권한 문제는 무시 (UI는 이미 반영됨)
        console.warn("Reaction delete ignored:", e);
      }
    });

    // 4. 토글(취소) 확인: 같은 아이콘을 눌렀다면 삭제만 하고 끝
    const isToggle = myReactions.some(m => m.msg.emoticon === reaction);
    if (isToggle) {
      onReactionUpdate?.();
      await Promise.all(deletePromises);
      return;
    }

    // 5. 변경/신규 추가: 삭제 완료를 기다리는 것이 안전 (순서 보장)
    await Promise.all(deletePromises);

    // 새 리액션 생성 및 추가
    let msg = new Message(room, My.user);
    msg.replyTo = parentMessage.msg.user;
    msg.replyToMessageId = parentMessage.msg.id;
    msg.parentMessageId = parentMessage.msg.parentMessageId ?? parentMessage.msg.id;
    msg.emoticon = reaction;

    const localReaction = new UIMessage(msg, []);
    localReaction.isLocal = true; // 🔧 로컬 로딩 상태 표시
    parentMessage.children.push(localReaction);
    onReactionUpdate?.();

    try {
      await Fire.msg.saveMessageAndNotify(msg);
    } catch (e) {
      console.error("Failed to save reaction:", e);
      // 실패 시 롤백 UI 처리가 필요할 수 있으나 여기서는 생략
    }
  }
}

export default MsgInputFooterReactions;
